import type React from "react";
import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  Alert,
  Paper,
  Divider,
  Chip,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  type SelectChangeEvent,
  CircularProgress,
} from "@mui/material";
import { format } from "date-fns/format";
import { isAfter } from "date-fns/isAfter";
import { differenceInMinutes } from "date-fns/differenceInMinutes";
import { isSameDay } from "date-fns/isSameDay";
import { isToday } from "date-fns/isToday";
import { isTomorrow } from "date-fns/isTomorrow";
import { setHours } from "date-fns/setHours";
import { setMinutes } from "date-fns/setMinutes";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventIcon from "@mui/icons-material/Event";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import AvailableSlots from "../AvailableSlots";
import { getAvailableSlots } from "../../apis/bookingApi";
import { extractTimeFromDateString } from "../../hooks/Date";
import type { BookingsByUser } from "../../schemas/bookings";
import {
  getBookingBlackoutDateObjects,
  isBookingBlackoutDate,
} from "../../utils/bookingBlackoutDates";

interface BookingItemProps extends BookingsByUser {
  hasBeenRescheduled?: boolean;
}

interface RescheduleFormProps {
  open: boolean;
  booking: BookingItemProps;
  onClose: () => void;
  onReschedule: (
    newStartTime: string,
    newEndTime: string,
    selectedDate: string,
    bookingStatus: string,
    email?: string
  ) => void;
}

const RescheduleBookingForm: React.FC<RescheduleFormProps> = ({
  open,
  booking,
  onClose,
  onReschedule,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [startTimeSlots, setStartTimeSlots] = useState<
    { value: string; label: string }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  const originalDurationMinutes = differenceInMinutes(
    new Date(booking.endTime),
    new Date(booking.startTime)
  );
  const originalDuration = originalDurationMinutes / 60; // Keep for display purposes

  // Generate start time slots for dropdown based on day of week
  const generateStartTimeSlots = useCallback(
    (date: Date) => {
      const dayOfWeek = format(date, "EEEE").toLowerCase();
      const businessHours = {
        monday: { start: "13:00", end: "22:00" },
        tuesday: { start: "13:00", end: "22:00" },
        wednesday: { start: "13:00", end: "22:00" },
        thursday: { start: "13:00", end: "22:00" },
        friday: { start: "13:00", end: "22:00" },
        saturday: { start: "10:00", end: "22:00" },
        sunday: { start: "10:00", end: "22:00" },
      };

      const hours = businessHours[dayOfWeek as keyof typeof businessHours];
      if (!hours) return [];

      const [startHour, startMinute] = hours.start.split(":").map(Number);
      const [endHour, endMinute] = hours.end.split(":").map(Number);

      const slots = [];
      const startTime24 = startHour * 60 + startMinute;
      const endTime24 = endHour * 60 + endMinute;

      for (let hour = 0; hour < 24; hour++) {
        for (const minute of [0, 30]) {
          const currentTime24 = hour * 60 + minute;
          // Ensure the slot allows for the exact original duration without exceeding business hours
          const endTimeAfterDuration = currentTime24 + originalDurationMinutes;
          if (
            currentTime24 >= startTime24 &&
            endTimeAfterDuration <= endTime24
          ) {
            const time = `${hour.toString().padStart(2, "0")}:${minute
              .toString()
              .padStart(2, "0")}`;
            const label = format(
              setMinutes(setHours(new Date(), hour), minute),
              "h:mm a"
            );
            slots.push({ value: time, label });
          }
        }
      }

      return slots;
    },
    [originalDurationMinutes]
  );


  const passSelectedDate = (date: string) => {
    const newDate = new Date(date);
    const year = newDate.getFullYear();
    const month = newDate.getMonth() + 1;
    const day = newDate.getDate();
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };


  // Calculate end time based on start time and original duration
  const calculateEndTime = useCallback(
    (date: Date, startTime: string) => {
      if (!startTime) return "";

      const [startHours, startMinutes] = startTime.split(":").map(Number);
      const startDateTime = new Date(date);
      startDateTime.setHours(startHours, startMinutes, 0, 0);

      // Add the original duration in minutes
      const endDateTime = new Date(
        startDateTime.getTime() + originalDurationMinutes * 60000
      );
      const endHours = endDateTime.getHours();
      const endMinutes = endDateTime.getMinutes();

      return `${endHours.toString().padStart(2, "0")}:${endMinutes
        .toString()
        .padStart(2, "0")}`;
    },
    [originalDurationMinutes]
  );

  // Handle date selection
  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setIsLoading(true);
    setStartTime("");
    setEndTime("");
    setStartTimeSlots([]);

    if (date) {
      // Generate start time slots
      const newStartTimeSlots = generateStartTimeSlots(date);
      setStartTimeSlots(newStartTimeSlots);

      if (newStartTimeSlots.length > 0) {
        // Set the first available start time
        const firstStartTime = newStartTimeSlots[0].value;
        setStartTime(firstStartTime);

        // Calculate end time based on the first start time
        const newEndTime = calculateEndTime(date, firstStartTime);
        setEndTime(newEndTime);
      }
    }

    setIsLoading(false);
  };

  // Handle start time change
  const handleStartTimeChange = (event: SelectChangeEvent) => {
    const newStartTime = event.target.value;
    setStartTime(newStartTime);

    if (selectedDate) {
      const newEndTime = calculateEndTime(selectedDate, newStartTime);
      setEndTime(newEndTime);
      setIsFormValid(true);
    }
  };

  const getCurrentDuration = (): {
    hours: number;
    minutes: number;
    total: number;
  } => {
    if (!startTime || !endTime)
      return {
        hours: Math.floor(originalDuration),
        minutes: originalDurationMinutes % 60,
        total: originalDuration,
      };

    try {
      const [startHours, startMinutes] = startTime.split(":").map(Number);
      const [endHours, endMinutes] = endTime.split(":").map(Number);

      const startDate = new Date();
      startDate.setHours(startHours, startMinutes, 0, 0);

      const endDate = new Date();
      endDate.setHours(endHours, endMinutes, 0, 0);

      if (endDate < startDate) {
        endDate.setDate(endDate.getDate() + 1);
      }

      const totalMinutes = differenceInMinutes(endDate, startDate);
      return {
        hours: Math.floor(totalMinutes / 60),
        minutes: totalMinutes % 60,
        total: totalMinutes / 60,
      };
    } catch (e) {
      return {
        hours: Math.floor(originalDuration),
        minutes: originalDurationMinutes % 60,
        total: originalDuration,
      };
    }
  };

  const validateForm = useCallback((): boolean => {
    if (!selectedDate) {
      setError("Please select a date to see available time slots");
      return false;
    }

    if (isBookingBlackoutDate(selectedDate)) {
      setError("We're closed on this date. Please choose another day.");
      return false;
    }

    if (!startTime || !endTime) {
      setError("Please select a start time");
      return false;
    }

    try {
      const [startHours, startMinutes] = startTime.split(":").map(Number);
      const [endHours, endMinutes] = endTime.split(":").map(Number);

      const startDateTime = new Date(selectedDate);
      startDateTime.setHours(startHours, startMinutes, 0, 0);

      const endDateTime = new Date(selectedDate);
      endDateTime.setHours(endHours, endMinutes, 0, 0);

      if (endDateTime < startDateTime) {
        endDateTime.setDate(endDateTime.getDate() + 1);
      }

      const now = new Date();

      if (!isAfter(endDateTime, startDateTime)) {
        setError("End time must be after start time");
        return false;
      }

      if (!isAfter(startDateTime, now)) {
        setError("Booking must be for a future time");
        return false;
      }

      const newDuration = differenceInMinutes(endDateTime, startDateTime);

      if (newDuration !== originalDurationMinutes) {
        const hours = Math.floor(originalDurationMinutes / 60);
        const minutes = originalDurationMinutes % 60;
        let durationText = "";
        if (hours > 0) durationText += `${hours} hour${hours !== 1 ? "s" : ""}`;
        if (minutes > 0)
          durationText += `${hours > 0 ? " " : ""}${minutes} minute${
            minutes !== 1 ? "s" : ""
          }`;
        setError(
          `Rescheduled booking duration must be exactly ${durationText}.`
        );
        return false;
      }

      const originalStart = new Date(booking.startTime);
      const originalEnd = new Date(booking.endTime);

      if (
        isSameDay(startDateTime, originalStart) &&
        format(startDateTime, "HH:mm") === format(originalStart, "HH:mm") &&
        format(endDateTime, "HH:mm") === format(originalEnd, "HH:mm")
      ) {
        setError("New time cannot be the same as the original");
        return false;
      }

      setError(null);
      return true;
    } catch (e) {
      setError("Invalid time format");
      return false;
    }
  }, [selectedDate, startTime, endTime, booking, originalDurationMinutes]);

  useEffect(() => {
    if (selectedDate && startTime && endTime) {
      setIsFormValid(validateForm());
    } else {
      setIsFormValid(false);
    }
  }, [selectedDate, startTime, endTime, validateForm]);

const handleSubmit = async () => {
  if (!validateForm() || !selectedDate) {
    return;
  }

  try {
    setIsLoading(true);
    setError(null); // Clear any previous errors

    if (!booking.laneOrCage.id) {
      throw new Error("Lane/Cage ID is missing.");
    }

    // Check for overlapping slots
    const response = await getAvailableSlots(
      selectedDate ? passSelectedDate(selectedDate.toString()) : "",
      booking.laneOrCage.id
    );
    const bookedSlots = response.data.bookedSlots || [];

    const timeToMinutes = (time: string): number => {
      const [hours, minutes] = time.split(":").map(Number);
      return hours * 60 + minutes;
    };

    const hasOverlap = bookedSlots.some((slot: any) => {
      const selectedStart = timeToMinutes(startTime);
      const selectedEnd = timeToMinutes(endTime);
      const slotStart = timeToMinutes(
        extractTimeFromDateString(slot.startTime)
      );
      const slotEnd = timeToMinutes(extractTimeFromDateString(slot.endTime));

      return (
        (selectedStart >= slotStart && selectedStart < slotEnd) ||
        (selectedEnd > slotStart && selectedEnd <= slotEnd) ||
        (selectedStart <= slotStart && selectedEnd >= slotEnd)
      );
    });

    if (hasOverlap) {
      setError(
        "Sorry, the selected time slot is already booked. Please choose a different time."
      );
      return; // Don't set loading to false here, let the finally block handle it
    }

    // Create the DateTime objects
    const [startHours, startMinutes] = startTime.split(":").map(Number);
    const startDateTime = new Date(selectedDate.toISOString());
    startDateTime.setHours(startHours, startMinutes, 0, 0);

    const [endHours, endMinutes] = endTime.split(":").map(Number);
    const endDateTime = new Date(selectedDate);
    endDateTime.setHours(endHours, endMinutes, 0, 0);

    if (endDateTime < startDateTime) {
      endDateTime.setDate(endDateTime.getDate() + 1);
    }

    // Call the reschedule function
    await onReschedule(
      startTime,
      endTime,
      passSelectedDate(selectedDate.toString()),
      "rescheduled",
      booking.userDetails.email
    );

    // Close the dialog after successful reschedule
    onClose();
    
  } catch (error) {
    console.error("Error during rescheduling:", error);
    setError("An error occurred while rescheduling. Please try again.");
  } finally {
    // Always set loading to false in the finally block
    setIsLoading(false);
  }
};

  const getDayLabel = (date: Date | undefined) => {
    if (!date) return "";
    if (isToday(date)) return "Today";
    if (isTomorrow(date)) return "Tomorrow";
    return format(date, "EEEE, MMMM d");
  };

  const getFormattedNewTime = () => {
    if (!selectedDate || !startTime || !endTime) return "...";

    try {
      const [startHours, startMinutes] = startTime.split(":").map(Number);
      const startDateTime = new Date(selectedDate);
      startDateTime.setHours(startHours, startMinutes, 0, 0);

      const [endHours, endMinutes] = endTime.split(":").map(Number);
      const endDateTime = new Date(selectedDate);
      endDateTime.setHours(endHours, endMinutes, 0, 0);

      let formattedDate = getDayLabel(selectedDate);
      if (endDateTime < startDateTime) {
        endDateTime.setDate(endDateTime.getDate() + 1);
        formattedDate += ` - ${format(endDateTime, "EEEE, MMMM d")}`;
      }

      return `${formattedDate}, ${format(startDateTime, "h:mm a")} - ${format(
        endDateTime,
        "h:mm a"
      )}`;
    } catch (e) {
      return "...";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "primary.main",
          color: "primary.contrastText",
          py: 2,
        }}
      >
        Reschedule Your Booking
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          <DialogContentText
            sx={{ mb: 3, display: "flex", alignItems: "center" }}
          >
            <InfoOutlinedIcon sx={{ mr: 1, color: "info.main" }} />
            Please select a new date and time for your booking. You can only
            reschedule a booking once. The duration of your rescheduled booking
            will be the same as the original booking. You cannot change the
            duration while rescheduling.
          </DialogContentText>

          <Paper
            elevation={3}
            sx={{ p: 2, mb: 3, bgcolor: "grey.50", borderRadius: 2 }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Current Booking
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <EventIcon color="action" />
              <Typography variant="body2">
                {format(new Date(booking.startTime), "EEEE, MMMM d, yyyy")}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mt: 1 }}
            >
              <AccessTimeIcon color="action" />
              <Typography variant="body2">
                {format(new Date(booking.startTime), "h:mm a")} -{" "}
                {format(new Date(booking.endTime), "h:mm a")} (
                {Math.floor(originalDuration) > 0
                  ? `${Math.floor(originalDuration)} hour${
                      Math.floor(originalDuration) !== 1 ? "s" : ""
                    }`
                  : ""}
                {originalDurationMinutes % 60 > 0
                  ? `${Math.floor(originalDuration) > 0 ? " " : ""}${
                      originalDurationMinutes % 60
                    } minute${originalDurationMinutes % 60 !== 1 ? "s" : ""}`
                  : ""}
                )
              </Typography>
            </Stack>
          </Paper>
        </Box>

        <Divider />

        <Grid container sx={{ p: 3 }}>
          <Grid item xs={12} md={7}>
            <Typography
              variant="subtitle1"
              sx={{
                mb: 2,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
              }}
            >
              <EventIcon sx={{ mr: 1 }} />
              Select New Date
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  flexGrow: 1,
                  position: "relative",
                }}
              >
                {isLoading && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
                <style>
                  {`
                    .rdp-day_selected {
                      background-color: var(--primary-color, #1976d2) !important;
                      color: white !important;
                      border-radius: 50% !important;
                      font-weight: bold !important;
                    }
                    .rdp-day_today {
                      border: 1px solid var(--primary-color, #1976d2) !important;
                      color: var(--primary-color, #1976d2) !important;
                      border-radius: 50% !important;
                    }
                    .rdp-button {
                      border-radius: 50% !important;
                      width: 40px !important;
                      height: 40px !important;
                      display: flex !important;
                      align-items: center !important;
                      justify-content: center !important;
                    }
                  `}
                </style>
                <DayPicker
                  mode="single"
                  selected={selectedDate}
                  onDayClick={handleDateSelect}
                  onSelect={handleDateSelect}
                  disabled={[
                    { before: new Date() },
                    ...getBookingBlackoutDateObjects(),
                  ]}
                  classNames={{
                    day_selected: "rdp-day_selected",
                    day_today: "rdp-day_today",
                    button: "rdp-button",
                  }}
                  styles={{
                    head_cell: { color: "var(--primary-color)" },
                    cell: { margin: "2px" },
                  }}
                  footer={
                    !selectedDate && (
                      <Typography variant="caption" color="text.secondary">
                        Please select a day to see available time slots.
                      </Typography>
                    )
                  }
                />
              </Paper>

              {selectedDate && (
                <Box
                  sx={{
                    ml: 2,
                    mt: 2,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Chip
                    label={format(selectedDate, "PPP")}
                    color="primary"
                    size="medium"
                    variant="outlined"
                    icon={<EventIcon />}
                  />
                </Box>
              )}
            </Box>
            <Paper
              elevation={3}
              sx={{
                p: 2,
                width: "fit-content",
                mb: 3,
                bgcolor: "grey.50",
                borderRadius: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Hours of Operation:
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <EventIcon color="action" />
                <Typography variant="body2">
                  Monday - Thursday: 1:00 PM - 10:00 PM <br />
                  Friday: 1:00 PM - 10:00 PM <br />
                  Saturday: 10:00 AM - 10:00 PM <br />
                  Sunday: 10:00 AM - 10:00 PM
                </Typography>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box sx={{ pl: { xs: 0, md: 3 }, mt: { xs: 2, md: 0 } }}>
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <AccessTimeIcon sx={{ mr: 1 }} />
                Select New Time
              </Typography>

              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  position: "relative",
                }}
              >
                {isLoading && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}

                {!selectedDate ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Please select a date first to see available time slots
                  </Alert>
                ) : (
                  <>
                    <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                      <InputLabel id="start-time-label">Start Time</InputLabel>
                      <Select
                        labelId="start-time-label"
                        id="start-time-select"
                        value={startTime}
                        onChange={handleStartTimeChange}
                        label="Start Time"
                        disabled={startTimeSlots.length === 0}
                      >
                        {startTimeSlots.map((slot) => (
                          <MenuItem key={slot.value} value={slot.value}>
                            {slot.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {startTime && endTime && (
                      <>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          <strong>End Time:</strong>{" "}
                          {format(
                            setHours(
                              setMinutes(
                                new Date(),
                                Number.parseInt(endTime.split(":")[1])
                              ),
                              Number.parseInt(endTime.split(":")[0])
                            ),
                            "h:mm a"
                          )}
                        </Typography>

                        <Box
                          sx={{
                            mt: 3,
                            p: 2,
                            bgcolor: "grey.100",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="subtitle2" color="text.primary">
                            Duration:{" "}
                            {(() => {
                              const duration = getCurrentDuration();
                              let text = "";
                              if (duration.hours > 0)
                                text += `${duration.hours} hour${
                                  duration.hours !== 1 ? "s" : ""
                                }`;
                              if (duration.minutes > 0)
                                text += `${duration.hours > 0 ? " " : ""}${
                                  duration.minutes
                                } minute${duration.minutes !== 1 ? "s" : ""}`;
                              return text || "0 minutes";
                            })()}
                          </Typography>
                        </Box>

                        <AvailableSlots
                          selectedDate={selectedDate ? passSelectedDate(selectedDate.toString()) : undefined}
                          startTime={startTime}
                          endTime={endTime}
                          listingId={booking.laneOrCage.id}
                        />
                      </>
                    )}
                  </>
                )}
              </Paper>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 4, mx: 3, mb: 3 }}>
                {error}
              </Alert>
            )}
          </Grid>
        </Grid>

        <Divider />

        <Box sx={{ p: 3, bgcolor: "grey.50" }}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "primary.light",
            }}
          >
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Booking Summary
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={5}>
                <Typography variant="body2" color="text.secondary">
                  From:
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {format(new Date(booking.startTime), "MMM d, h:mm a")} -{" "}
                  {format(new Date(booking.endTime), "h:mm a")}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                sm={2}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CompareArrowsIcon
                  color="action"
                  sx={{
                    transform: { xs: "rotate(90deg)", sm: "rotate(0deg)" },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={5}>
                <Typography variant="body2" color="text.secondary">
                  To:
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  color="primary.dark"
                >
                  {getFormattedNewTime()}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </DialogContent>

      {/* <DialogActions sx={{ px: 3, py: 2, bgcolor: "grey.50" }}>
        <Button onClick={onClose} color="inherit" variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={!isFormValid} sx={{ px: 4 }}>
          Confirm Reschedule
        </Button>
      </DialogActions> */}

      <DialogActions sx={{ px: 3, py: 2, bgcolor: "grey.50" }}>
        <Button
          onClick={onClose}
          color="inherit"
          variant="outlined"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!isFormValid || isLoading}
          sx={{ px: 4, position: "relative" }}
        >
          {isLoading ? (
            <>
              <CircularProgress
                size={24}
                color="inherit"
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
              Rescheduling...
            </>
          ) : (
            "Confirm Reschedule"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RescheduleBookingForm;
